import React, { createContext, useContext, useRef } from 'react';
import { useSessionStorage } from 'react-use';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { Person, CalibrationSuggestion } from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import CalibrationNudgeModal, {
  CalibrationNudgeVariant,
} from 'talent_matching/components/modals/calibration_nudge_modal';
import { useTalentMatchingTabContext } from './talent_matching_tab_context';
import { useCalibrationModal } from './calibration_modal_context';

type AdvancedCandidateCountContextType = {
  onAdvanceSuccess: (application: Person) => boolean;
  onBulkAdvanceSuccess: (applications: Person[]) => void;
};

const AdvancedCandidateCountContext =
  createContext<AdvancedCandidateCountContextType>({
    onAdvanceSuccess: () => false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBulkAdvanceSuccess: () => {},
  });

type Props = {
  openCalibrationModal: () => void;
  children: React.ReactNode;
  nudgeVariant?: CalibrationNudgeVariant;
};

export const AdvancedCandidateCountProvider = ({
  openCalibrationModal,
  children,
  nudgeVariant = 'initial',
}: Props) => {
  const { userId, hiringPlanId } = useUserContext();
  const { canCreateCalibrationSetting } = useTalentMatchingTabContext();
  const { setNudgeCalloutState, setPendingNudgeSuggestions } =
    useCalibrationModal();

  const [
    showCalibrationNudgeModal,
    openCalibrationNudgeModal,
    closeCalibrationNudgeModal,
  ] = useModal();

  const [advancedCandidateCount, setAdvancedCandidateCount] =
    useSessionStorage<number>(
      `advanced_candidate_count:${userId}:${hiringPlanId}`,
      0
    );

  // Keep a ref so concurrent callbacks always read the latest count without
  // relying on stale render-time closure values.
  const countRef = useRef(advancedCandidateCount);
  countRef.current = advancedCandidateCount;

  const isPartialOrLimitedMatch = (application: Person) =>
    application.matchStrength === 'partial_match' ||
    application.matchStrength === 'limited_match';

  const incrementCount = (by: number): boolean => {
    const NUDGE_THRESHOLD = 5;
    if (countRef.current >= NUDGE_THRESHOLD || by <= 0) {
      return false;
    }
    const increment = Math.min(by, NUDGE_THRESHOLD - countRef.current);
    countRef.current += increment;
    setAdvancedCandidateCount(countRef.current);
    if (countRef.current >= NUDGE_THRESHOLD) {
      openCalibrationNudgeModal();
      return true;
    }
    return false;
  };

  const onAdvanceSuccess = (application: Person): boolean => {
    if (isPartialOrLimitedMatch(application)) {
      return incrementCount(1);
    }
    return false;
  };

  const onBulkAdvanceSuccess = (applications: Person[]) => {
    const partialOrLimitedCount = applications.filter(
      isPartialOrLimitedMatch
    ).length;
    incrementCount(partialOrLimitedCount);
  };

  const onApplySuggestions = (selected: CalibrationSuggestion[]) => {
    if (selected.length > 0) {
      setPendingNudgeSuggestions(selected);
    }
    setNudgeCalloutState('applied');
  };

  const onManualReview = () => {
    setNudgeCalloutState('manual_review');
  };

  return (
    <AdvancedCandidateCountContext.Provider
      value={{ onAdvanceSuccess, onBulkAdvanceSuccess }}
    >
      {children}
      <CalibrationNudgeModal
        isOpen={showCalibrationNudgeModal}
        onClose={closeCalibrationNudgeModal}
        canCreateCalibrationSetting={canCreateCalibrationSetting}
        openCalibrationModal={openCalibrationModal}
        onApplySuggestions={onApplySuggestions}
        onManualReview={onManualReview}
        variant={nudgeVariant}
      />
    </AdvancedCandidateCountContext.Provider>
  );
};

export const useAdvancedCandidateCount = () =>
  useContext(AdvancedCandidateCountContext);
